import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  NgZone,
} from "@angular/core";
import { SharedModule } from "../../shared/shared.module";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
  IAppPluginWidget,
  IPlugin,
  ModelService,
  IAstimaxAccount,
  IUserNames,
  PluginInjectorWidget,
  PluginInjectorService,
  WidgetExtraTitleDirective,
  PluginCommunicationService,
  PluginRegistry,
} from "core-client/api";
import {
  SipService,
  ActiveCallInfo,
  IncomingCallCanceledEvent,
  VideoUpgradeRequest,
} from "../../shared/services/sip/sip.service";
import { JabraService } from "../../shared/services/jabra/jabra.service";
import { Subject, Subscription, interval, takeUntil } from "rxjs";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import * as FontAwesomeIcons from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { VideoDisplayComponent } from "../video-display/video-display.component";
import { VideoControlsComponent } from "../video-controls/video-controls.component";
import {
  KeypadDialogComponent,
  KeypadDialogData,
  KeypadDialogResult,
} from "../keypad-dialog/keypad-dialog.component";
import { VideoDeviceService } from "../../shared/services/video/video-device.service";
import { AudioDeviceService } from "../../shared/services/audio-device/audio-device.service";
import { ToneService } from "../../shared/services/tone/tone.service";
import {
  formatForDisplay,
  toDialable,
  isInternalExtension,
  normalizeToE164,
} from "../utils/phone-number-format";

interface CallTimer {
  callId: string;
  startTime: number;
  duration: number;
}

@Component({
  selector: "lib-phone-widget-view",
  imports: [SharedModule, VideoDisplayComponent, VideoControlsComponent],
  templateUrl: "./phone-widget-view.component.html",
  styleUrls: ["./phone-widget-view.component.scss"],
})
export class PhoneWidgetViewComponent
  extends WidgetExtraTitleDirective
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() data!: PluginInjectorWidget.IWidget;
  @Input() isHeaderHovered: boolean = false;

  private destroyPluginEventSubscription$ = new Subject<void>();

  // Font Awesome Icons
  faPhoneAlt: IconDefinition = FontAwesomeIcons.faPhone;
  faPhoneSlash: IconDefinition = FontAwesomeIcons.faPhoneSlash;
  faMicrophone: IconDefinition = FontAwesomeIcons.faMicrophone;
  faMicrophoneSlash: IconDefinition = FontAwesomeIcons.faMicrophoneSlash;
  faPause: IconDefinition = FontAwesomeIcons.faPause;
  faPlay: IconDefinition = FontAwesomeIcons.faPlay;
  faExchangeAlt: IconDefinition = FontAwesomeIcons.faExchangeAlt;
  faLink: IconDefinition = FontAwesomeIcons.faLink;
  faUsers: IconDefinition = FontAwesomeIcons.faUsers;
  faUsersFriends: IconDefinition = FontAwesomeIcons.faUserFriends;
  faPhoneIncoming: IconDefinition = FontAwesomeIcons.faPhone;
  faPhoneOutgoing: IconDefinition = FontAwesomeIcons.faPhone;
  faPhoneVolume: IconDefinition = FontAwesomeIcons.faPhoneVolume;
  faRedoAlt: IconDefinition = FontAwesomeIcons.faRedoAlt;
  faCheckCircle: IconDefinition = FontAwesomeIcons.faCheckCircle;
  faExclamationCircle: IconDefinition = FontAwesomeIcons.faExclamationCircle;
  faTimes: IconDefinition = FontAwesomeIcons.faTimes;
  faCircle: IconDefinition = FontAwesomeIcons.faCircle;
  faSpinner: IconDefinition = FontAwesomeIcons.faSpinner;
  faIncomingRinging: IconDefinition = FontAwesomeIcons.faPhoneVolume;
  faOutgoingRinging: IconDefinition = FontAwesomeIcons.faPhone;
  faPauseCircle: IconDefinition = FontAwesomeIcons.faPauseCircle;
  faTimesCircle: IconDefinition = FontAwesomeIcons.faTimesCircle;
  faBan: IconDefinition = FontAwesomeIcons.faBan;
  faInfoCircle: IconDefinition = FontAwesomeIcons.faInfoCircle;
  faSync: IconDefinition = FontAwesomeIcons.faSync;
  faClock: IconDefinition = FontAwesomeIcons.faClock;
  faVideo: IconDefinition = FontAwesomeIcons.faVideo;
  faVideoSlash: IconDefinition = FontAwesomeIcons.faVideoSlash;
  faAngleRight: IconDefinition = FontAwesomeIcons.faAngleRight;
  faTh: IconDefinition = FontAwesomeIcons.faTh;
  faBackspace: IconDefinition = FontAwesomeIcons.faBackspace;

  // Component state
  /*permissions = {
    edit: false
  };*/
  phoneWidget!: IAppPluginWidget;

  targetNumber: string = "";
  activeCalls: ActiveCallInfo[] = [];
  activeCallId: string | null = null;
  private lastDialedNumber: string = "";
  private lastCallWasVideo: boolean = false;
  isTooSmall: boolean = false;

  // Video state
  hasActiveVideoCall: boolean = false;
  isVideoFullscreen: boolean = false;
  areVideoControlsVisible: boolean = true;
  availableCameras: MediaDeviceInfo[] = [];
  isWidgetHeaderFullscreenButtonVisible: boolean = true; // Widget header fullscreen button is visible when not in edit mode

  // Video capabilities (optimistic - assume true until proven otherwise)
  canStartVideoCall: boolean = true;
  private videoCapabilitiesChecked: boolean = false;

  // Video upgrade request
  videoUpgradeRequest: VideoUpgradeRequest | null = null;

  // DND state
  isDoNotDisturbEnabled: boolean = false;
  showPresenceSelector: boolean = false;

  // Phone number of SIP account
  private accountPhoneNumber: string = "";

  // Call timers
  private callTimers: Map<string, CallTimer> = new Map();
  private timerSubscription?: Subscription;

  // Audio input device availability
  audioInputUnavailable: boolean = false;

  // Device reconnection notification
  showReconnectionNotification: boolean = false;

  // Jabra headset integration
  jabra = {
    initialized: false,
    connected: false,
    deviceName: null as string | null,
    muted: false,
    onHold: false,
  };

  // DTMF keyboard feedback
  lastDtmfKey: { key: string; id: number } | null = null;
  private dtmfKeyCounter = 0;

  // Flags for preventing circular dependencies
  private handlingJabraCallEvent = false;
  private handlingJabraHoldEvent = false;
  private handlingJabraMuteEvent = false;
  private currentlyAcceptingCall = false;

  // Track if a call is being rejected to prevent duplicated rejection signals
  private callRejectionInProgress = false;

  // Track incoming calls that have been signaled to the headset
  private signaledIncomingCalls = new Set<string>();

  // Track last headset button press time for call waiting scenarios
  private lastHeadsetButtonPressTime = 0;
  private readonly DOUBLE_CLICK_THRESHOLD = 500; // milliseconds

  private subscriptions: Subscription[] = [];

  @ViewChild("phoneContainer", { static: true }) phoneContainer:
    | ElementRef
    | undefined;
  @ViewChild("videoSection") videoSection: ElementRef | undefined;
  @ViewChild(VideoControlsComponent) videoControlsComponent:
    | VideoControlsComponent
    | undefined;
  private resizeObserver: ResizeObserver | undefined;

  constructor(
    private readonly translator: TranslateService,
    private pluginInjectorService: PluginInjectorService,
    private pluginCommunicationService: PluginCommunicationService,
    private modelService: ModelService,
    public sipService: SipService,
    private breakpointObserver: BreakpointObserver,
    public jabraService: JabraService,
    private ngZone: NgZone,
    private videoDeviceService: VideoDeviceService,
    private audioDeviceService: AudioDeviceService,
    private toneService: ToneService,
    private dialog: MatDialog,
  ) {
    super();
  }

  private static readonly VALID_DTMF_KEYS = new Set([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "*",
    "#",
  ]);

  @HostListener("document:keydown", ["$event"])
  handleDtmfKeydown(event: KeyboardEvent): void {
    if (!this.hasActiveVideoCall || !this.activeCallId) return;

    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    )
      return;

    if (!PhoneWidgetViewComponent.VALID_DTMF_KEYS.has(event.key)) return;

    event.preventDefault();
    this.sendDtmfWithFeedback(event.key);
  }

  onMobileDtmfInput(key: string): void {
    if (!this.hasActiveVideoCall || !this.activeCallId) return;
    this.sendDtmfWithFeedback(key);
  }

  private sendDtmfWithFeedback(key: string): void {
    if (!this.activeCallId) return;
    this.toneService.playDtmfTone(key);
    this.sipService.sendDTMF(this.activeCallId, key);
    this.lastDtmfKey = { key, id: ++this.dtmfKeyCounter };
  }

  @HostListener("window:keyup.enter", ["$event"])
  handleEnterKey(event: KeyboardEvent) {
    if (this.canMakeCall() && event.target instanceof HTMLInputElement) {
      this.startCall();
    }
  }

  @HostListener("window:keyup.escape", ["$event"])
  handleEscapeKey(event: KeyboardEvent) {
    this.closePresenceSelector();
  }

  @HostListener("window:click", ["$event"])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (
      this.showPresenceSelector &&
      !target.closest(".presence-selector") &&
      !target.closest(".call-status-indicator")
    ) {
      this.closePresenceSelector();
    }
  }

  reloadPage(): void {
    window.location.reload();
  }

  ngAfterViewInit(): void {
    this.observeResizeChanges();
  }

  /*private async updatePermissions(): Promise<void> {
    const currentUser = this.modelService._user.getUser();
    const appDashboard = currentUser.app_dashboards.find(
      (dashboard: IAppDashboard) => dashboard._id === currentUser.active_dashboard
    );

    if (appDashboard) {
      const pluginPermissions = await this.pluginInjectorService.permissionService.getPermissions(
        appDashboard, currentUser
      );

      const accessRights = pluginPermissions.find(
        (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === this.data.pluginId
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[this.data.pluginModule.toLowerCase()] as IPermissionsFlags;
        this.permissions = {
          edit: modulePermissions['astimax_phone_edit']
        };
      }
    }
  }*/

  get hasLastDialedNumber(): boolean {
    return !!this.lastDialedNumber;
  }

  /**
   * Get user display name based on user names
   * Priority: fullName > userName > empty string
   * Used for local video label and SIP From header display name
   */
  get userDisplayName(): string {
    const userNames = this.phoneWidget?.extraData?.["userNames"] as
      | IUserNames
      | undefined;
    if (userNames?.fullName) {
      return userNames.fullName;
    }
    if (userNames?.userName) {
      return userNames.userName;
    }
    return "";
  }

  /**
   * Check if the call name contains the originally dialed number pattern "CallerName > DialedNumber"
   * @param call The call information object
   * @returns True if the pattern is present
   */
  hasOriginallyDialedNumber(call: ActiveCallInfo): boolean {
    return !!call.name && call.name.includes(" > ");
  }

  /**
   * Get the caller display part (name with number in parentheses)
   * Used when hasOriginallyDialedNumber() returns true
   * @param call The call information object
   * @returns Formatted caller string, e.g., "John (12345)"
   */
  getCallerDisplayPart(call: ActiveCallInfo): string {
    if (!call.name) {
      return (
        formatForDisplay(call.number) ||
        this.translator.instant("ASTIMAX.PHONE.WIDGET.CALL.UNKNOWN")
      );
    }

    if (call.name.includes(" > ")) {
      const [callerPart] = call.name.split(" > ", 2);
      return `${callerPart} (${formatForDisplay(call.number)})`;
    }

    return call.name;
  }

  /**
   * Get the originally dialed number part
   * Used when hasOriginallyDialedNumber() returns true
   * @param call The call information object
   * @returns The originally dialed number, or empty string if not present
   */
  getOriginallyDialedPart(call: ActiveCallInfo): string {
    if (call.name && call.name.includes(" > ")) {
      const [, dialedPart] = call.name.split(" > ", 2);
      return formatForDisplay(dialedPart);
    }
    return "";
  }

  /**
   * Format call display name based on name and number
   * Note: For calls with originally dialed number pattern, use template with icon instead
   * @param call The call information object
   * @returns Formatted display string based on requirements
   */
  getCallDisplayName(call: ActiveCallInfo): string {
    // If both name and number are empty, show "Unknown"
    if (!call.name && !call.number) {
      return this.translator.instant("ASTIMAX.PHONE.WIDGET.CALL.UNKNOWN");
    }

    // If name is empty or name equals number, just show number
    if (!call.name || call.name === call.number) {
      return formatForDisplay(call.number);
    }

    // For "CallerName > OriginallyDialedNumber" format, this method still works
    // but prefer using the template with icon for better UX
    if (call.name.includes(" > ")) {
      const [callerPart, dialedPart] = call.name.split(" > ", 2);
      return `${callerPart} (${formatForDisplay(call.number)}) > ${formatForDisplay(dialedPart)}`;
    }

    // Otherwise show "name (number)" format
    return `${call.name} (${formatForDisplay(call.number)})`;
  }

  /**
   * Get the phone number from SIP account
   * @returns The phone number as a string, or empty string if not available
   */
  getAccountPhoneNumber(): string {
    return this.accountPhoneNumber;
  }

  /**
   * Extract the phone number from the SIP user by removing leading non-numeric characters
   * @param sipUser The SIP user string
   * @returns The phone number as a string
   */
  private extractPhoneNumberFromSipUser(sipUser: string | undefined): string {
    if (!sipUser) {
      return "";
    }

    // Remove all leading non-digit characters
    const phoneNumber = sipUser.replace(/^\D+/, "");
    return phoneNumber;
  }

  getButtonText(isVideo: boolean): string {
    if (this.targetNumber) {
      return this.translator.instant(
        isVideo
          ? "ASTIMAX.PHONE.WIDGET.CONTROLS.VIDEO_CALL"
          : "ASTIMAX.PHONE.WIDGET.CONTROLS.CALL",
      );
    }
    return this.translator.instant(
      this.hasLastDialedNumber
        ? "ASTIMAX.PHONE.WIDGET.CONTROLS.REDIAL"
        : isVideo
          ? "ASTIMAX.PHONE.WIDGET.CONTROLS.VIDEO_CALL"
          : "ASTIMAX.PHONE.WIDGET.CONTROLS.CALL",
    );
  }

  onTargetNumberBlur(): void {
    if (this.targetNumber && !isInternalExtension(this.targetNumber)) {
      this.targetNumber = formatForDisplay(normalizeToE164(this.targetNumber));
    }
  }

  async ngOnInit(): Promise<void> {
    this.phoneWidget = this.data.appPluginWidget;
    //await this.updatePermissions();

    // Load DND state from localStorage
    this.loadDoNotDisturbState();

    // Note: Video capabilities are detected lazily when user initiates a video call
    // to avoid requesting camera permission on page load

    // Setup SIP service subscriptions
    this.subscriptions.push(
      this.sipService.getActiveCalls().subscribe((calls) => {
        // Check for new calls to start or update timers
        calls.forEach((call) => {
          const existingCall = this.activeCalls.find((c) => c.id === call.id);

          // Start timer for new calls except incoming ringing calls and call waiting calls
          if (
            !existingCall &&
            !(
              call.isIncoming &&
              (call.state === "ringing" || call.state === "call-waiting")
            )
          ) {
            this.startCallTimer(call.id);
          }

          // Start timer for incoming calls that just got connected
          if (
            existingCall &&
            existingCall.isIncoming &&
            (existingCall.state === "ringing" ||
              existingCall.state === "call-waiting") &&
            call.state === "connected"
          ) {
            this.startCallTimer(call.id);
          }
        });

        // Check for ended calls and stop timers
        this.activeCalls.forEach((oldCall) => {
          if (!calls.find((c) => c.id === oldCall.id)) {
            this.stopCallTimer(oldCall.id);
          }
        });

        this.activeCalls = calls;
        // Show video for:
        // 1. Outgoing calls with video (even while ringing - caller already chose video)
        // 2. Connected/held calls with video
        // Don't show for:
        // 1. Incoming ringing calls (user needs to accept first)
        // 2. Call waiting calls (user needs to accept first)
        // 3. Calls with pending video upgrade request (user needs to accept first)
        this.hasActiveVideoCall = calls.some((call) => {
          const hasVideoCapability = call.hasVideo || call.hasRemoteVideo;
          if (!hasVideoCapability) return false;

          // Don't show video if there's a pending video upgrade for this call
          if (this.videoUpgradeRequest?.callId === call.id) {
            return false;
          }

          // For incoming calls, only show video if already accepted (not ringing/call-waiting)
          if (call.isIncoming) {
            return call.state !== "ringing" && call.state !== "call-waiting";
          }

          // For outgoing calls, show video immediately (including ringing state)
          return true;
        });
      }),

      this.sipService
        .getActiveCallId()
        .subscribe((id) => (this.activeCallId = id)),

      // Subscribe to available cameras
      this.videoDeviceService
        .getAvailableVideoInputDevices()
        .subscribe((cameras) => {
          this.availableCameras = cameras;
        }),

      // Subscribe to audio input availability
      this.audioDeviceService
        .getAudioInputAvailable()
        .subscribe((available) => {
          this.audioInputUnavailable = !available;
        }),
    );

    // Subscribe to incoming call canceled events from SIP service
    if (this.sipService.incomingCallCanceled$) {
      this.subscriptions.push(
        this.sipService.incomingCallCanceled$.subscribe(
          (event: IncomingCallCanceledEvent | null) => {
            if (event) {
              // Only signal rejection to headset for regular incoming calls, not call waiting calls
              if (event.isCallWaiting) {
                console.log(
                  `Call waiting call ${event.callId} was canceled - not signaling headset rejection`,
                );
              } else {
                console.log(
                  `Regular incoming call ${event.callId} was canceled - signaling headset rejection`,
                );
                // Mark that a rejection is in progress to prevent duplicate signals
                this.callRejectionInProgress = true;

                // Reset the flag after a delay to allow the system to stabilize
                setTimeout(() => {
                  this.callRejectionInProgress = false;
                }, 1000);
              }
            }
          },
        ),
      );
    }

    // Subscribe to video upgrade requests from SIP service
    if (this.sipService.videoUpgradeRequested$) {
      this.subscriptions.push(
        this.sipService.videoUpgradeRequested$.subscribe(
          (request: VideoUpgradeRequest | null) => {
            if (request) {
              console.log(
                `[PhoneWidget] Video upgrade requested for call ${request.callId}`,
              );
              this.videoUpgradeRequest = request;
            } else {
              this.videoUpgradeRequest = null;
            }
            // Recalculate hasActiveVideoCall since pending upgrade state changed
            this.recalculateHasActiveVideoCall();
          },
        ),
      );
    }

    // Start timer subscription for all calls
    this.setupTimerSubscription();

    // Setup responsive layout subscription
    this.subscriptions.push(
      this.breakpointObserver
        .observe(["(max-width: 250px)", "(max-height: 125px)"])
        .subscribe((result) => {
          // Hide main content and show only status bar when width or height is constrained
          this.isTooSmall =
            result.breakpoints["(max-width: 250px)"] ||
            result.breakpoints["(max-height: 125px)"];
        }),
    );

    // Initialize SIP service if Astimax account data is provided
    if (this.phoneWidget.extraData?.["astimaxAccount"] != null) {
      const astimaxAccount = this.phoneWidget.extraData?.[
        "astimaxAccount"
      ] as IAstimaxAccount;
      console.log(astimaxAccount);
      // Extract phone number from SIP user
      this.accountPhoneNumber = this.extractPhoneNumberFromSipUser(
        astimaxAccount.sipUser,
      );

      // Fetch ASTIMAX plugin settings for ICE configuration
      let iceStunServerUrl: string | undefined;
      let iceTurnServerUrl: string | undefined;
      let iceTurnUsername: string | undefined;
      let iceTurnPassword: string | undefined;
      try {
        const plugins = await this.modelService._plugin.listAll();
        const astimaxPlugin = (plugins as IPlugin[]).find(
          (p) => p.name === "ASTIMAX",
        );
        const iceSettings = astimaxPlugin?.settings;
        if (iceSettings) {
          iceStunServerUrl =
            (iceSettings["ice_stun_server_url"] as string) || undefined;
          iceTurnServerUrl =
            (iceSettings["ice_turn_server_url"] as string) || undefined;
          iceTurnUsername =
            (iceSettings["ice_turn_username"] as string) || undefined;
          iceTurnPassword =
            (iceSettings["ice_turn_password"] as string) || undefined;
        }
      } catch (error) {
        console.error(
          "Error fetching ASTIMAX plugin settings for ICE config:",
          error,
        );
      }

      await this.sipService.initialize({
        authUser: astimaxAccount.sipUser ?? "",
        authPassword: astimaxAccount.sipPassword ?? "",
        registrar: astimaxAccount.registrar ?? "",
        transportUrl: astimaxAccount.registrar?.trim()
          ? `wss://${astimaxAccount.registrar.trim()}/ws/asterisk`
          : "",
        userAgent: "ADDboard Astimax Phone",
        displayName: this.userDisplayName, // Priority: fullName > userName > empty string
        iceStunServerUrl,
        iceTurnServerUrl,
        iceTurnUsername,
        iceTurnPassword,
      });

      // Set DND mode in SIP service after initialization
      this.sipService.setDoNotDisturb(this.isDoNotDisturbEnabled);
    } else {
      console.error(
        "Error initializing sipService: no Astimax account data provided",
      );
    }

    // Listen for ASTIMAX plugin settings changes (e.g., ICE server config)
    this.subscriptions.push(
      this.modelService._plugin
        .observePlugin$()
        .subscribe(({ plugin, event }) => {
          if (
            event === "update" &&
            plugin.name === "ASTIMAX" &&
            plugin.settings
          ) {
            const settings = plugin.settings;
            this.sipService.updateIceServers({
              iceStunServerUrl:
                (settings["ice_stun_server_url"] as string) || undefined,
              iceTurnServerUrl:
                (settings["ice_turn_server_url"] as string) || undefined,
              iceTurnUsername:
                (settings["ice_turn_username"] as string) || undefined,
              iceTurnPassword:
                (settings["ice_turn_password"] as string) || undefined,
            });
          }
        }),
    );

    // Listen for fullscreen changes (e.g., when user exits with ESC key)
    this.setupFullscreenListener();

    // Initialize and set up Jabra integration
    this.initializeJabra();

    // Listen for action events on the plugin communication bus
    this.initializePluginCommunicationEvents();
  }

  /**
   * Initialize video capabilities detection (lazy - only called when needed)
   */
  private async initializeVideoCapabilities(): Promise<void> {
    // Only check once
    if (this.videoCapabilitiesChecked) {
      return;
    }

    this.videoCapabilitiesChecked = true;

    try {
      const capability = await this.sipService.detectVideoCapabilities();
      this.canStartVideoCall = capability.hasCamera && capability.hasVideoCodec;
      console.log("[PhoneWidget] Video capabilities detected:", capability);
    } catch (error) {
      console.error("Error detecting video capabilities:", error);
      this.canStartVideoCall = false;
    }
  }

  /**
   * Initializes event listener for the plugin communication bus
   */
  private initializePluginCommunicationEvents(): void {
    // Start a call
    this.pluginCommunicationService
      .onAction(
        PluginRegistry.Astimax.Actions.PhoneModule.START_CALL,
        "astimax",
      )
      .pipe(takeUntil(this.destroyPluginEventSubscription$))
      .subscribe((payload) => {
        const activeCall = this.getActiveCall();
        if (!activeCall || !activeCall?.isConference) {
          this.targetNumber = payload.targetNumber;
          this.startCall(payload.withVideo);
        }
      });
    // Pick a call
    this.pluginCommunicationService
      .onAction(PluginRegistry.Astimax.Actions.PhoneModule.PICK_CALL, "astimax")
      .pipe(takeUntil(this.destroyPluginEventSubscription$))
      .subscribe((payload) => {
        const activeCall = this.getActiveCall();
        if (!activeCall || !activeCall?.isConference) {
          this.targetNumber = "*8" + payload.targetNumber;
          this.startCall();
        }
      });
    // Blind transfer a call
    this.pluginCommunicationService
      .onAction(
        PluginRegistry.Astimax.Actions.PhoneModule.BLIND_TRANSFER_CALL,
        "astimax",
      )
      .pipe(takeUntil(this.destroyPluginEventSubscription$))
      .subscribe((payload) => {
        const activeCall = this.getActiveCall();
        if (activeCall) {
          this.targetNumber = payload.targetNumber;
          this.blindTransfer(activeCall.id, this.targetNumber);
        }
      });

    // Handle request to toggle video fullscreen
    this.pluginCommunicationService.handleRequests("astimax", {
      tryToggleVideoFullscreen: () => {
        console.log("[PhoneWidget] Received tryToggleVideoFullscreen request");

        // Check if there's an active video call
        if (this.hasActiveVideoCall && this.activeCallId) {
          console.log(
            "[PhoneWidget] Active video call found, toggling video fullscreen",
          );
          this.toggleVideoFullscreen();
          return true; // Indicate that we handled the fullscreen toggle
        }

        console.log("[PhoneWidget] No active video call, returning false");
        return false; // Indicate that we did not handle it (fallback to widget fullscreen)
      },
    });
  }

  /**
   * Initializes the Jabra integration and sets up event subscriptions
   */
  private async initializeJabra(): Promise<void> {
    try {
      await this.jabraService.initialize();

      // Set up Jabra subscriptions
      this.subscriptions.push(
        // SDK initialization status
        this.jabraService.isSdkInitialized$.subscribe((initialized) => {
          this.ngZone.run(() => {
            this.jabra.initialized = initialized;
            console.log(`Jabra SDK initialized: ${initialized}`);
          });
        }),

        // Connected status
        this.jabraService.isConnected$.subscribe((connected) => {
          this.ngZone.run(() => {
            this.jabra.connected = connected;
            console.log(`Jabra device connected: ${connected}`);
          });
        }),

        // Device name
        this.jabraService.deviceName$.subscribe((name) => {
          this.ngZone.run(() => {
            this.jabra.deviceName = name;
            console.log(`Jabra device name: ${name}`);
          });
        }),

        // Mute state
        this.jabraService.isMuted$.subscribe((muted) => {
          this.ngZone.run(() => {
            this.jabra.muted = muted;
            console.log(`Jabra mute state: ${muted}`);

            if (!this.handlingJabraMuteEvent) {
              // Sync with active call
              this.handleJabraMuteChange(muted);
            }
          });
        }),

        // Hold state
        this.jabraService.isOnHold$.subscribe((onHold) => {
          this.ngZone.run(() => {
            this.jabra.onHold = onHold;
            console.log(`Jabra hold state: ${onHold}`);
          });
        }),

        // Accept call events
        this.jabraService.onAcceptCall$.subscribe(() => {
          this.ngZone.run(() => {
            console.log("Jabra headset: Accept call event received");
            this.handleJabraAcceptCall();
          });
        }),

        // Reject call events
        this.jabraService.onRejectCall$.subscribe(() => {
          this.ngZone.run(() => {
            console.log("Jabra headset: Reject call event received");
            // Only handle reject events if no call rejection is already in progress
            if (!this.callRejectionInProgress) {
              this.handleJabraRejectCall();
            }
          });
        }),

        // End call events
        this.jabraService.onEndCall$.subscribe(() => {
          this.ngZone.run(() => {
            console.log("Jabra headset: End call event received");
            this.handleJabraEndCall();
          });
        }),

        // Toggle hold events
        this.jabraService.onToggleHold$.subscribe((holdState) => {
          this.ngZone.run(() => {
            console.log(
              `Jabra headset: Toggle hold event received (${holdState})`,
            );
            this.handleJabraHoldChange(holdState);
          });
        }),

        // Toggle mute events
        this.jabraService.onToggleMute$.subscribe((muteState) => {
          this.ngZone.run(() => {
            console.log(
              `Jabra headset: Toggle mute event received (${muteState})`,
            );
            this.handlingJabraMuteEvent = true;
            this.handleJabraMuteChange(muteState);

            // Reset flag after a small delay to prevent circular updates
            setTimeout(() => {
              this.handlingJabraMuteEvent = false;
            }, 500);
          });
        }),

        // Swap calls events
        this.jabraService.onSwapCalls$.subscribe((activeCalls) => {
          this.ngZone.run(() => {
            console.log("Jabra headset: Swap calls event received");
            this.handleJabraSwapCalls(activeCalls);
          });
        }),

        // Device reconnection events
        this.jabraService.onDeviceReconnected$.subscribe(() => {
          this.ngZone.run(() => {
            console.log("Jabra device was reconnected - notification needed");
            // Show reconnection notification
            this.showReconnectionNotification = true;
            // Auto-hide after 15 seconds
            setTimeout(() => {
              this.showReconnectionNotification = false;
            }, 15000);
          });
        }),

        // Cancel outgoing ringing calls events (during headset reset)
        this.jabraService.onCancelOutgoingRingingCall$.subscribe(() => {
          this.ngZone.run(() => {
            console.log(
              "Jabra headset: Cancel outgoing ringing calls event received",
            );
            this.handleJabraCancelOutgoingRingingCalls();
          });
        }),

        // Reject incoming ringing calls events (during headset reset)
        this.jabraService.onRejectIncomingRingingCall$.subscribe(() => {
          this.ngZone.run(() => {
            console.log(
              "Jabra headset: Reject incoming ringing calls event received",
            );
            this.handleJabraRejectIncomingRingingCalls();
          });
        }),
      );

      // Monitor for incoming calls to signal to headset
      this.subscriptions.push(
        this.sipService.getActiveCalls().subscribe((calls) => {
          // Look for incoming calls (including call waiting)
          const incomingCalls = calls.filter(
            (call) =>
              call.isIncoming &&
              (call.state === "ringing" || call.state === "call-waiting"),
          );

          if (incomingCalls.length > 0 && this.jabra.connected) {
            // Get the first incoming call we haven't signaled yet
            const newIncomingCall = incomingCalls.find(
              (call) => !this.signaledIncomingCalls.has(call.id),
            );

            if (newIncomingCall) {
              console.log(
                `Signaling incoming call to Jabra headset: ${newIncomingCall.number} (ID: ${newIncomingCall.id}, State: ${newIncomingCall.state})`,
              );

              // Mark this call as signaled so we don't try again
              this.signaledIncomingCalls.add(newIncomingCall.id);

              // Only signal to headset for regular incoming calls, not call waiting
              // Call waiting should be handled more subtly
              if (newIncomingCall.state === "ringing") {
                this.jabraService.signalIncomingCall().catch((err) => {
                  console.error(
                    "Error signaling incoming call to headset:",
                    err,
                  );
                });
              } else {
                console.log(
                  "Call waiting call - not signaling to headset to avoid conflicts",
                );
              }
            }
          }

          // Clean up the tracking set when calls are removed
          if (this.signaledIncomingCalls.size > 0) {
            const activeCallIds = new Set(calls.map((call) => call.id));
            for (const signalId of this.signaledIncomingCalls) {
              if (!activeCallIds.has(signalId)) {
                this.signaledIncomingCalls.delete(signalId);
              }
            }
          }
        }),
      );
    } catch (error) {
      console.error("Error initializing Jabra service:", error);
    }
  }

  // Hide reconnection notification
  hideReconnectionNotification(): void {
    this.showReconnectionNotification = false;
  }

  /**
   * Handle cancel outgoing ringing calls event from Jabra headset
   * This is triggered during headset reset to ensure any outgoing calls are properly canceled
   */
  private handleJabraCancelOutgoingRingingCalls(): void {
    // Find any outgoing calls in ringing state
    const outgoingRingingCalls = this.activeCalls.filter(
      (call) => !call.isIncoming && call.state === "ringing",
    );

    if (outgoingRingingCalls.length > 0) {
      console.log(
        `Found ${outgoingRingingCalls.length} outgoing ringing calls to cancel due to headset reset`,
      );

      // Cancel each call
      outgoingRingingCalls.forEach((call) => {
        console.log(
          `Canceling outgoing ringing call ${call.id} due to headset reset`,
        );
        this.endCall(call.id);
      });
    } else {
      console.log("No outgoing ringing calls to cancel");
    }
  }

  /**
   * Handle reject incoming ringing calls event from Jabra headset
   * This is triggered during headset reset to ensure any incoming calls are properly rejected
   */
  private handleJabraRejectIncomingRingingCalls(): void {
    // Find any incoming calls in ringing or call-waiting state
    const incomingRingingCalls = this.activeCalls.filter(
      (call) =>
        call.isIncoming &&
        (call.state === "ringing" || call.state === "call-waiting"),
    );

    if (incomingRingingCalls.length > 0) {
      console.log(
        `Found ${incomingRingingCalls.length} incoming ringing/waiting calls to reject due to headset reset`,
      );

      // Reject each call
      incomingRingingCalls.forEach((call) => {
        console.log(
          `Rejecting incoming ringing/waiting call ${call.id} due to headset reset`,
        );
        this.endCall(call.id);
      });
    } else {
      console.log("No incoming ringing/waiting calls to reject");
    }
  }

  /**
   * Handle accept call event from Jabra headset
   */
  private handleJabraAcceptCall(): void {
    // If we're already in the process of accepting a call, don't trigger another accept
    if (this.currentlyAcceptingCall) {
      console.log("Already accepting a call - ignoring headset accept event");
      return;
    }

    // Find incoming call to accept (prioritize regular ringing over call waiting)
    let incomingCall = this.activeCalls.find(
      (call) => call.isIncoming && call.state === "ringing",
    );
    if (!incomingCall) {
      incomingCall = this.activeCalls.find(
        (call) => call.isIncoming && call.state === "call-waiting",
      );
    }

    if (incomingCall) {
      // Set flag to indicate we're handling a Jabra event
      this.handlingJabraCallEvent = true;

      console.log(
        `Accepting ${incomingCall.state === "call-waiting" ? "call waiting" : "incoming"} call ${incomingCall.id} from headset`,
      );

      // Accept the call (using appropriate method based on call state)
      if (incomingCall.state === "call-waiting") {
        // For call waiting, don't signal back to headset to avoid conflicts
        this.sipService.acceptCallWaiting(incomingCall.id).catch((error) => {
          console.error("Error accepting call waiting from headset:", error);
        });
      } else {
        this.acceptCall();
      }

      // Reset flags after call
      setTimeout(() => {
        this.handlingJabraCallEvent = false;
      }, 500);
    } else {
      console.log("No incoming or waiting call found to accept from headset");
    }
  }

  /**
   * Handle reject call event from Jabra headset
   */
  private handleJabraRejectCall(): void {
    // Skip if call rejection is already in progress
    if (this.callRejectionInProgress) {
      console.log(
        "Call rejection already in progress - ignoring headset reject event",
      );
      return;
    }

    // Find incoming call to reject (prioritize regular ringing over call waiting)
    let incomingCall = this.activeCalls.find(
      (call) => call.isIncoming && call.state === "ringing",
    );
    if (!incomingCall) {
      incomingCall = this.activeCalls.find(
        (call) => call.isIncoming && call.state === "call-waiting",
      );
    }

    if (incomingCall) {
      this.handlingJabraCallEvent = true;
      // Set rejection in progress flag to prevent duplicate signals
      this.callRejectionInProgress = true;
      this.endCall(incomingCall.id);
      setTimeout(() => {
        this.handlingJabraCallEvent = false;
        // Reset the flag after a delay to allow the system to stabilize
        setTimeout(() => {
          this.callRejectionInProgress = false;
        }, 500);
      }, 500);
    }
  }

  /**
   * Handle end call event from Jabra headset
   */
  private handleJabraEndCall(): void {
    // If we're currently in the process of accepting a call, ignore end call signals
    // to prevent race conditions during call waiting acceptance
    if (this.currentlyAcceptingCall) {
      console.log(
        "Ignoring headset end call event during call acceptance process",
      );
      return;
    }

    this.handlingJabraCallEvent = true;

    // Check for call waiting scenario: active call + waiting call
    const connectedCalls = this.activeCalls.filter(
      (call) => call.state === "connected" || call.state === "conference",
    );
    const waitingCalls = this.activeCalls.filter(
      (call) => call.isIncoming && call.state === "call-waiting",
    );
    const heldCalls = this.activeCalls.filter((call) => call.state === "held");

    // If there's an active call AND a waiting call, treat first button press as "accept waiting"
    if (connectedCalls.length > 0 && waitingCalls.length > 0) {
      const currentTime = Date.now();
      const timeSinceLastPress = currentTime - this.lastHeadsetButtonPressTime;

      console.log(
        `Call waiting scenario detected. Time since last press: ${timeSinceLastPress}ms`,
      );

      // If this is a double-click (within threshold), reject the waiting call
      if (timeSinceLastPress < this.DOUBLE_CLICK_THRESHOLD) {
        console.log("Double-click detected - rejecting waiting call");
        const waitingCall = waitingCalls[0];
        this.endCall(waitingCall.id);
      } else {
        // Single click - accept the waiting call
        console.log("Single click detected - accepting waiting call");
        const waitingCall = waitingCalls[0];

        // Set flag to prevent re-entry
        this.currentlyAcceptingCall = true;

        // Accept the call waiting
        this.sipService
          .acceptCallWaiting(waitingCall.id)
          .then(async () => {
            console.log(
              "Successfully accepted call waiting via headset button",
            );

            // IMPORTANT: Since the Jabra SDK doesn't properly support call waiting,
            // we need to work around its limitation. Instead of trying to signal
            // acceptIncomingCall (which fails), we directly signal an outgoing call
            // to make the headset recognize there's an active call.
            if (this.jabra.connected) {
              try {
                // Give the headset a moment to process
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Always signal an outgoing call to fix the headset state
                // This ensures the busy light stays on and call control works
                console.log(
                  "Signaling outgoing call to fix headset state after call waiting acceptance",
                );
                await this.jabraService.signalOutgoingCall();
              } catch (error) {
                console.error(
                  "Error updating headset state after accepting waiting call:",
                  error,
                );
              }
            }
          })
          .catch((error) => {
            console.error("Error accepting call waiting via headset:", error);
          })
          .finally(() => {
            // Reset flag
            setTimeout(() => {
              this.currentlyAcceptingCall = false;
            }, 500);
          });
      }

      this.lastHeadsetButtonPressTime = currentTime;
    }
    // SPECIAL CASE: If there are only held calls and no connected calls, end the held call directly
    else if (heldCalls.length > 0 && connectedCalls.length === 0) {
      console.log("Only held calls remaining - ending held call directly");
      const heldCall = heldCalls[0];
      this.endCall(heldCall.id);
    } else {
      // Normal end call behavior (no call waiting scenario)

      // First check for any ringing calls (both incoming and outgoing)
      const ringingCall = this.activeCalls.find(
        (call) => call.state === "ringing" && !call.isCallWaiting,
      );
      if (ringingCall) {
        console.log(
          `Ending ringing call ${ringingCall.id} due to headset end call event`,
        );
        this.endCall(ringingCall.id);
      }
      // Then check for established calls
      else {
        // End active call if there is one
        const activeCallId = this.activeCallId;
        if (activeCallId) {
          console.log(
            `Ending call ${activeCallId} due to headset end call event`,
          );

          // Check if there will be remaining calls after this one ends
          const remainingCallsAfterEnd = this.activeCalls.filter(
            (c) => c.id !== activeCallId,
          );
          const willHaveRemainingCalls = remainingCallsAfterEnd.length > 0;

          // End the call
          this.endCall(activeCallId);

          // IMPORTANT: If there's a remaining held call, update headset state
          // so the next button press will properly end it
          if (willHaveRemainingCalls) {
            const remainingCall = remainingCallsAfterEnd[0];
            if (remainingCall.state === "held") {
              console.log(
                "Remaining call is held - updating headset state for next button press",
              );

              // Wait for the call end to complete
              setTimeout(async () => {
                if (this.jabra.connected) {
                  try {
                    // Signal that there's still a call (even though it's held)
                    // This keeps the busy light on and allows the next button press to work
                    await this.jabraService.signalOutgoingCall();
                    console.log(
                      "Updated headset state for remaining held call",
                    );
                  } catch (error) {
                    console.error(
                      "Error updating headset state for held call:",
                      error,
                    );
                  }
                }
              }, 300);
            }
          }
        } else if (this.activeCalls.length > 0) {
          // Fallback: try to find any call to end
          const callToEnd = this.activeCalls[0];
          console.log(
            `Ending call ${callToEnd.id} (fallback) due to headset end call event`,
          );
          this.endCall(callToEnd.id);
        } else {
          console.log("No active call to end");
        }
      }
    }

    setTimeout(() => {
      this.handlingJabraCallEvent = false;
    }, 500);
  }

  /**
   * Handle hold state change from Jabra headset
   * @param holdState True to hold, false to resume
   */
  private handleJabraHoldChange(holdState: boolean): void {
    // Set flag to prevent circular updates
    this.handlingJabraHoldEvent = true;

    const activeCallId = this.activeCallId;
    if (!activeCallId) {
      console.log("No active call to hold/resume");
      this.handlingJabraHoldEvent = false;
      return;
    }

    const call = this.activeCalls.find((c) => c.id === activeCallId);
    if (!call) {
      console.log("Active call not found");
      this.handlingJabraHoldEvent = false;
      return;
    }

    // Check if state change is needed
    const currentlyOnHold = call.state === "held";
    if (currentlyOnHold === holdState) {
      console.log(`SIP call hold state already matches headset (${holdState})`);
      this.handlingJabraHoldEvent = false;
      return;
    }

    console.log(`Updating SIP call hold state to match headset: ${holdState}`);

    // Perform hold/resume operation
    if (holdState) {
      // Need to hold the call
      this.sipService
        .holdCall(activeCallId)
        .catch((err) => {
          console.error("[Jabra Service] Error putting call on hold:", err);
        })
        .finally(() => {
          // Reset flag when operation completes
          setTimeout(() => {
            this.handlingJabraHoldEvent = false;
          }, 500);
        });
    } else {
      // Need to resume the call
      this.sipService
        .resumeCall(activeCallId)
        .catch((err) => {
          console.error("[Jabra Service] Error resuming call:", err);
        })
        .finally(() => {
          // Reset flag when operation completes
          setTimeout(() => {
            this.handlingJabraHoldEvent = false;
          }, 500);
        });
    }
  }

  /**
   * Handle mute state change from Jabra headset
   * @param muteState True to mute, false to unmute
   */
  private handleJabraMuteChange(muteState: boolean): void {
    const activeCallId = this.activeCallId;
    if (!activeCallId) {
      return;
    }

    const call = this.activeCalls.find((c) => c.id === activeCallId);
    if (call && call.isMuted !== muteState) {
      console.log(
        `Updating SIP call mute state to match headset: ${muteState}`,
      );
      this.toggleMute(activeCallId);
    }
  }

  /**
   * Handle swap calls event from Jabra headset
   */
  private handleJabraSwapCalls(activeCalls: ActiveCallInfo[]): void {
    // Need at least two calls to swap
    if (activeCalls.length < 2) {
      console.log("Not enough calls to swap");
      return;
    }

    // Find the current active call and a held call
    const activeCall = activeCalls.find((call) => call.state === "connected");
    const heldCall = activeCalls.find((call) => call.state === "held");

    if (activeCall && heldCall) {
      console.log(
        `Swapping calls: active=${activeCall.id}, held=${heldCall.id}`,
      );

      // First hold the active call
      this.sipService
        .holdCall(activeCall.id)
        .then(() => {
          // Then resume the held call
          return this.sipService.resumeCall(heldCall.id);
        })
        .then(() => {
          // Update the active call ID
          this.selectCall(heldCall.id);
        })
        .catch((err) => {
          console.error("Error swapping calls:", err);
        });
    } else {
      console.log("Could not find appropriate calls to swap");
    }
  }

  // DND methods
  togglePresenceSelector(event: MouseEvent): void {
    event.stopPropagation();
    this.showPresenceSelector = !this.showPresenceSelector;
  }

  closePresenceSelector(): void {
    this.showPresenceSelector = false;
  }

  setDoNotDisturb(enabled: boolean): void {
    this.isDoNotDisturbEnabled = enabled;
    this.sipService.setDoNotDisturb(enabled);
    this.saveDoNotDisturbState();
    this.closePresenceSelector();
  }

  saveDoNotDisturbState(): void {
    localStorage.setItem(
      "phoneDoNotDisturb",
      this.isDoNotDisturbEnabled.toString(),
    );
  }

  loadDoNotDisturbState(): void {
    const savedState = localStorage.getItem("phoneDoNotDisturb");
    if (savedState !== null) {
      this.isDoNotDisturbEnabled = savedState === "true";
    }
  }

  private observeResizeChanges(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      // Update responsive state based on container size
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;

        // Check if we'd have vertical overflow
        let hasVerticalOverflow = false;
        if (this.activeCalls.length > 0) {
          // Estimate minimum height needed for calls view (approximate calculation)
          const minHeightNeeded = 125; // Status bar + basic controls
          hasVerticalOverflow = height < minHeightNeeded;
        }

        this.isTooSmall = width < 250 || height < 125 || hasVerticalOverflow;
      }

      this.setPhoneContainerOnHover(this.isHeaderHovered);
    });

    if (this.phoneContainer?.nativeElement) {
      this.resizeObserver.observe(this.phoneContainer?.nativeElement);
    }
  }

  private setPhoneContainerOnHover(isHeaderHovered: boolean): void {
    if (this.phoneContainer?.nativeElement) {
      this.phoneContainer.nativeElement.style.marginTop = isHeaderHovered
        ? "35px"
        : "0px";
    }
  }

  // Get appropriate icon for call status in status bar
  getCallStatusIcon(call: ActiveCallInfo): any {
    if (call.isIncoming && call.state === "ringing") {
      return this.faIncomingRinging;
    } else if (call.isIncoming && call.state === "call-waiting") {
      return this.faClock;
    } else if (!call.isIncoming && call.state === "ringing") {
      return this.faOutgoingRinging;
    } else if (call.state === "held") {
      return this.faPauseCircle;
    } else if (call.state === "transferring") {
      return this.faExchangeAlt;
    } else if (call.isConference || call.state === "conference") {
      return this.faUsers;
    } else {
      return this.faPhoneAlt;
    }
  }

  // Get appropriate icon for idle state considering DND mode
  getIdleStatusIcon(): any {
    return this.isDoNotDisturbEnabled ? this.faBan : this.faPhoneAlt;
  }

  // Get tooltip text for status icon
  getStatusTooltip(): string {
    if (!this.sipService.isRegistered) {
      return this.translator.instant(
        "ASTIMAX.PHONE.WIDGET.CALL.STATE.NOT_REGISTERED",
      );
    }

    const activeCall = this.getActiveCall();
    if (activeCall) {
      return this.getCallStateText(activeCall);
    }

    return this.translator.instant(
      this.isDoNotDisturbEnabled
        ? "ASTIMAX.PHONE.WIDGET.PRESENCE_STATE.DO_NOT_DISTURB"
        : "ASTIMAX.PHONE.WIDGET.PRESENCE_STATE.AVAILABLE",
    );
  }

  // Call timer functions
  private setupTimerSubscription(): void {
    // Update timers every second
    this.timerSubscription = interval(1000).subscribe(() => {
      const now = Date.now();

      this.callTimers.forEach((timer, callId) => {
        if (timer.startTime > 0) {
          timer.duration = Math.floor((now - timer.startTime) / 1000);
          // Force change detection by creating a new reference
          this.callTimers.set(callId, { ...timer });
        }
      });
    });
  }

  private startCallTimer(callId: string): void {
    if (!this.callTimers.has(callId)) {
      this.callTimers.set(callId, {
        callId,
        startTime: Date.now(),
        duration: 0,
      });
    }
  }

  private stopCallTimer(callId: string): void {
    this.callTimers.delete(callId);
  }

  /**
   * Recalculates hasActiveVideoCall based on current calls and pending video upgrade state.
   * Called when videoUpgradeRequest changes to ensure video UI is shown/hidden appropriately.
   */
  private recalculateHasActiveVideoCall(): void {
    this.hasActiveVideoCall = this.activeCalls.some((call) => {
      const hasVideoCapability = call.hasVideo || call.hasRemoteVideo;
      if (!hasVideoCapability) return false;

      // Don't show video if there's a pending video upgrade for this call
      if (this.videoUpgradeRequest?.callId === call.id) {
        return false;
      }

      // For incoming calls, only show video if already accepted (not ringing/call-waiting)
      if (call.isIncoming) {
        return call.state !== "ringing" && call.state !== "call-waiting";
      }

      // For outgoing calls, show video immediately (including ringing state)
      return true;
    });
  }

  // Only show timer for certain call states
  shouldShowCallTimer(call: ActiveCallInfo): boolean {
    // Don't show timer for incoming ringing calls or call waiting calls
    if (
      call.isIncoming &&
      (call.state === "ringing" || call.state === "call-waiting")
    ) {
      return false;
    }
    return true;
  }

  getCallDuration(callId: string): string {
    const timer = this.callTimers.get(callId);
    if (!timer) {
      return "00:00:00";
    }

    const seconds = timer.duration;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      secs.toString().padStart(2, "0"),
    ].join(":");
  }

  // UI helper functions
  getCallStateText(call: ActiveCallInfo): string {
    switch (call.state) {
      case "ringing":
        return this.translator.instant(
          call.isIncoming
            ? "ASTIMAX.PHONE.WIDGET.CALL.STATE.RINGING_INCOMING"
            : "ASTIMAX.PHONE.WIDGET.CALL.STATE.RINGING",
        );
      case "call-waiting":
        return this.translator.instant(
          "ASTIMAX.PHONE.WIDGET.CALL.STATE.CALL_WAITING",
        );
      case "connected":
        return this.translator.instant(
          "ASTIMAX.PHONE.WIDGET.CALL.STATE.CONNECTED",
        );
      case "held":
        return this.translator.instant(
          "ASTIMAX.PHONE.WIDGET.CALL.STATE.ONHOLD",
        );
      case "transferring":
        return this.translator.instant(
          "ASTIMAX.PHONE.WIDGET.CALL.STATE.TRANSFERRING",
        );
      case "conference":
        return this.translator.instant(
          "ASTIMAX.PHONE.WIDGET.CALL.STATE.CONFERENCE",
        );
      case "busy":
        return this.translator.instant("ASTIMAX.PHONE.WIDGET.CALL.STATE.BUSY");
      default:
        return "";
    }
  }

  // Only show state chip for certain states
  shouldShowStateChip(call: ActiveCallInfo): boolean {
    return (
      call.state === "held" ||
      call.state === "transferring" ||
      call.state === "conference" ||
      call.state === "call-waiting"
    );
  }

  // Check if audio should play for this call (only for audio active call)
  shouldPlayAudio(call: ActiveCallInfo): boolean {
    // Audio should play if:
    // 1. The call has a remote stream
    // 2. The call is connected or held
    // 3. The call is the audio active call (or if no audio active call is set, default to connected calls)
    if (!call.remoteStream) {
      return false;
    }

    if (call.state !== "connected" && call.state !== "held") {
      return false;
    }

    const audioActiveCallId = this.sipService.getAudioActiveCallId();

    // If no audio active call is set, default to playing audio for connected calls
    if (!audioActiveCallId) {
      return call.state === "connected";
    }

    // Only play audio for the audio active call
    const shouldPlay = call.id === audioActiveCallId;

    // Log only when audio state changes or for debugging call waiting scenarios
    /*if (shouldPlay && call.state === 'connected') {
      console.log(`Playing audio for call ${call.id} (audio active call: ${audioActiveCallId})`);
    }*/

    return shouldPlay;
  }

  // TrackBy function for ngFor optimization
  trackByCallId(index: number, call: ActiveCallInfo): string {
    return call.id;
  }

  // Call control methods
  canMakeCall(): boolean {
    if (this.audioInputUnavailable) {
      return false;
    }
    return (
      this.activeCalls.length === 0 ||
      (this.activeCalls.length === 1 && this.activeCalls[0].state !== "ringing")
    );
  }

  redialLastNumber(): void {
    if (this.hasLastDialedNumber) {
      this.targetNumber = this.lastDialedNumber;
    }
  }

  async startCall(withVideo: boolean = false): Promise<void> {
    if (!this.canMakeCall()) {
      return;
    }

    if (!this.targetNumber) {
      if (this.lastDialedNumber) {
        this.targetNumber = this.lastDialedNumber;
        return;
      }
      return;
    }

    try {
      // Put any connected calls on hold first
      for (const call of this.activeCalls) {
        if (call.state === "connected") {
          await this.sipService.holdCall(call.id);
        }
      }

      this.lastDialedNumber = this.targetNumber;
      this.lastCallWasVideo = withVideo;

      // Signal outgoing call to Jabra headset if connected
      if (this.jabra.connected) {
        await this.jabraService.signalOutgoingCall();
      }

      await this.sipService.makeCall(toDialable(this.targetNumber), withVideo);
      this.targetNumber = "";
    } catch (error) {
      console.error("Error starting call:", error);
    }
  }

  async acceptCall(): Promise<void> {
    // Set flag to prevent re-entry due to events during this call
    if (this.currentlyAcceptingCall) {
      console.log("Call acceptance already in progress - preventing duplicate");
      return;
    }

    try {
      this.currentlyAcceptingCall = true;

      // Get the call before we try to accept it
      const incomingCall = this.activeCalls.find(
        (call) => call.isIncoming && call.state === "ringing",
      );
      if (!incomingCall) {
        console.log("No incoming call to accept");
        return;
      }

      // For regular incoming calls, signal the headset first if this wasn't initiated by headset
      if (this.jabra.connected && !this.handlingJabraCallEvent) {
        await this.jabraService.signalCallAccepted();
        // Small delay to ensure headset state is updated
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Accept the call via SIP service
      await this.sipService.acceptCall();
    } catch (error) {
      console.error("Error accepting call:", error);
    } finally {
      // Reset the flags
      this.currentlyAcceptingCall = false;
    }
  }

  /**
   * Accept an incoming call as audio-only (receive remote video but don't send local video)
   * Creates an asymmetric video state where the callee can see the caller's video
   */
  async acceptCallAudioOnly(): Promise<void> {
    // Set flag to prevent re-entry due to events during this call
    if (this.currentlyAcceptingCall) {
      console.log("Call acceptance already in progress - preventing duplicate");
      return;
    }

    try {
      this.currentlyAcceptingCall = true;

      // Get the call before we try to accept it
      const incomingCall = this.activeCalls.find(
        (call) => call.isIncoming && call.state === "ringing",
      );
      if (!incomingCall) {
        console.log("No incoming call to accept");
        return;
      }

      // For regular incoming calls, signal the headset first if this wasn't initiated by headset
      if (this.jabra.connected && !this.handlingJabraCallEvent) {
        await this.jabraService.signalCallAccepted();
        // Small delay to ensure headset state is updated
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Accept the call as audio-only via SIP service
      await this.sipService.acceptCallAudioOnly();
    } catch (error) {
      console.error("Error accepting call as audio-only:", error);
    } finally {
      // Reset the flags
      this.currentlyAcceptingCall = false;
    }
  }

  /**
   * Accept a call waiting call and put active calls on hold
   * @param callId The ID of the call waiting call to accept
   * @param audioOnly If true, accept without sending local video (asymmetric video)
   */
  async acceptCallWaiting(
    callId: string,
    audioOnly: boolean = false,
  ): Promise<void> {
    // Set flag to prevent re-entry due to events during this call
    if (this.currentlyAcceptingCall) {
      console.log("Call acceptance already in progress - preventing duplicate");
      return;
    }

    try {
      this.currentlyAcceptingCall = true;

      // Verify the call still exists and is in call-waiting state
      const call = this.activeCalls.find(
        (c) => c.id === callId && c.state === "call-waiting",
      );
      if (!call) {
        console.log(
          "Call waiting call no longer available or not in waiting state",
        );
        return;
      }

      // For call waiting scenarios, we don't signal to the headset at all
      // Call waiting is handled subtly and the headset state will be updated
      // automatically when the call becomes established
      console.log(
        `Accepting call waiting call${audioOnly ? " (audio-only)" : ""} without headset signaling`,
      );

      // Accept the call waiting call (this will automatically put active calls on hold)
      await this.sipService.acceptCallWaiting(callId, audioOnly);

      console.log("Call waiting acceptance completed successfully");
    } catch (error) {
      console.error("Error accepting call waiting:", error);
    } finally {
      // Reset the flags
      this.currentlyAcceptingCall = false;
    }
  }

  /**
   * Accept a video upgrade request from the remote party
   */
  async acceptVideoUpgrade(): Promise<void> {
    if (!this.videoUpgradeRequest) {
      console.log("[PhoneWidget] No video upgrade request to accept");
      return;
    }

    try {
      console.log(
        `[PhoneWidget] Accepting video upgrade for call ${this.videoUpgradeRequest.callId}`,
      );
      await this.sipService.acceptVideoUpgrade(this.videoUpgradeRequest.callId);
      this.videoUpgradeRequest = null;
    } catch (error) {
      console.error("[PhoneWidget] Error accepting video upgrade:", error);
    }
  }

  /**
   * Decline a video upgrade request (accept as audio-only, creating asymmetric video)
   */
  async declineVideoUpgrade(): Promise<void> {
    if (!this.videoUpgradeRequest) {
      console.log("[PhoneWidget] No video upgrade request to decline");
      return;
    }

    try {
      console.log(
        `[PhoneWidget] Declining video upgrade for call ${this.videoUpgradeRequest.callId}`,
      );
      await this.sipService.declineVideoUpgrade(
        this.videoUpgradeRequest.callId,
      );
      this.videoUpgradeRequest = null;
    } catch (error) {
      console.error("[PhoneWidget] Error declining video upgrade:", error);
    }
  }

  async endCall(callId: string): Promise<void> {
    const call = this.activeCalls.find((c) => c.id === callId);
    if (!call) return;

    try {
      // Check if this is an incoming call in ringing or call-waiting state
      const isRejectingIncomingCall =
        call.isIncoming &&
        (call.state === "ringing" || call.state === "call-waiting");
      // Check if this is specifically a call waiting call
      const isCallWaitingCall =
        call.isCallWaiting || call.state === "call-waiting";

      // First handle the SIP service call ending
      await this.sipService.endCall(callId);

      // Check how many calls will remain after this call ends
      const remainingCallsAfterEnd = this.activeCalls.filter(
        (c) => c.id !== callId,
      );
      const willHaveRemainingCalls = remainingCallsAfterEnd.length > 0;

      // Special handling for headset depending on call type and remaining calls
      if (this.jabra.connected && !this.handlingJabraCallEvent) {
        if (isRejectingIncomingCall) {
          // Only signal rejection for regular incoming calls, not call waiting calls
          if (isCallWaitingCall) {
            console.log(
              "Rejecting call waiting call: Not signaling headset (call waiting handled subtly)",
            );
          } else {
            // For rejecting regular incoming calls, use the dedicated method
            // Only signal if we're not already in a call rejection process
            if (!this.callRejectionInProgress) {
              console.log(
                "Rejecting regular incoming call: Using dedicated rejection signal",
              );
              this.callRejectionInProgress = true;

              try {
                await this.jabraService.signalCallRejected();
              } catch (error) {
                const errorMessage =
                  error instanceof Error ? error.message : String(error);
                // Ignore "no incoming calls" errors as they're expected if the headset
                // was already notified about the rejection
                if (!errorMessage.includes("no incoming call")) {
                  console.error(
                    "Error signaling call rejection to headset:",
                    error,
                  );
                } else {
                  console.log(
                    "No incoming call to reject - call already rejected",
                  );
                }
              }

              // Reset the flag after a delay
              setTimeout(() => {
                this.callRejectionInProgress = false;
              }, 1000);
            } else {
              console.log(
                "Call rejection already in progress - skipping headset signal",
              );
            }
          }
        } else {
          // For normal call endings (not from headset)
          if (!willHaveRemainingCalls) {
            // This is the last call - signal call ended
            console.log(
              "UI-initiated call end: Signaling headset (this is the last call)",
            );
            await this.jabraService.signalCallEnded(true);
          } else {
            // We have remaining calls - need to ensure headset knows about them
            console.log(
              "UI-initiated call end: Other calls still active, updating headset state",
            );

            // Find the best remaining call (prefer connected/conference over held)
            const remainingConnectedCall = remainingCallsAfterEnd.find(
              (c) => c.state === "connected" || c.state === "conference",
            );
            const remainingCall =
              remainingConnectedCall || remainingCallsAfterEnd[0];

            // Signal to headset that there's still an active call
            // We use signalOutgoingCall to ensure the headset knows there's a call
            try {
              await this.jabraService.signalOutgoingCall();
              console.log("Signaled to headset that calls are still active");

              // If the remaining call is held, also update hold state
              if (remainingCall.state === "held") {
                await this.jabraService.signalHold(true);
              }
            } catch (error) {
              console.error(
                "Error updating headset state after call end:",
                error,
              );
            }
          }
        }
      } else {
        console.log("Headset-initiated call end: Not signaling headset");
      }
    } catch (error) {
      console.error("Error ending call:", error);
    }
  }

  async toggleHold(callId: string): Promise<void> {
    const call = this.activeCalls.find((c) => c.id === callId);
    if (!call) return;

    try {
      // Determine target state (opposite of current)
      const willBeOnHold = call.state !== "held";

      if (call.state === "held") {
        // Resume call
        await this.sipService.resumeCall(callId);
      } else {
        // Hold call
        await this.sipService.holdCall(callId);
      }

      // If this toggle wasn't initiated by headset, update headset state
      if (!this.handlingJabraHoldEvent && this.jabra.connected) {
        console.log(
          `UI-initiated hold toggle: Signaling headset (${willBeOnHold})`,
        );
        await this.jabraService.signalHold(willBeOnHold);
      } else {
        console.log(
          `Headset-initiated hold toggle: Not signaling headset back`,
        );
      }
    } catch (error) {
      console.error("Error toggling hold:", error);
    }
  }

  async toggleMute(callId: string): Promise<void> {
    try {
      // Check current mute state before toggling
      const call = this.activeCalls.find((c) => c.id === callId);
      if (!call) return;

      // Toggle mute state via SIP service
      await this.sipService.muteAudio(callId);

      // If this toggle wasn't initiated by the headset, update headset state
      if (!this.handlingJabraMuteEvent && this.jabra.connected) {
        // The call's mute state has been toggled by this point
        const newMuteState = !call.isMuted; // This is the new state after toggle

        console.log(
          `UI-initiated mute toggle: Signaling headset (${newMuteState})`,
        );
        await this.jabraService.signalMute(newMuteState);
      } else {
        console.log(
          `Headset-initiated mute toggle: Not signaling headset back`,
        );
      }
    } catch (error) {
      console.error("Error toggling mute:", error);
    }
  }

  async blindTransfer(callId: string, targetNumber: string) {
    if (!callId || !targetNumber) return;

    try {
      const call = this.activeCalls.find((c) => c.id === callId);
      if (!call) return;

      if (
        call.isIncoming &&
        (call.state === "ringing" || call.state === "call-waiting")
      ) {
        await this.sipService.transferIncomingCall(
          callId,
          toDialable(targetNumber),
        );
      } else {
        await this.sipService.blindTransfer(callId, toDialable(targetNumber));
      }

      this.targetNumber = "";
    } catch (error) {
      console.error("Error transferring call:", error);
    }
  }

  get canBridge(): boolean {
    return (
      this.activeCalls.length === 2 &&
      this.activeCalls.every(
        (call) => call.state === "connected" || call.state === "held",
      )
    );
  }

  async bridgeCalls() {
    if (!this.canBridge) return;

    try {
      await this.sipService.bridgeCalls();
    } catch (error) {
      console.error("Error bridging calls:", error);
    }
  }

  getActiveCall(): ActiveCallInfo | null {
    return (
      this.activeCalls.find((call) => call.id === this.activeCallId) || null
    );
  }

  async selectCall(callId: string): Promise<void> {
    if (callId === this.activeCallId) {
      return;
    }

    try {
      for (const call of this.activeCalls) {
        if (call.id === callId) {
          if (call.state === "held") {
            await this.sipService.resumeCall(call.id);

            // Update Jabra headset hold state
            if (this.jabra.connected) {
              await this.jabraService.signalHold(false);
            }
          } else if (call.state === "call-waiting") {
            // Handle call waiting selection by accepting the call
            await this.acceptCallWaiting(call.id);
            return; // Early return as acceptCallWaiting handles the state update
          }
          this.activeCallId = call.id;
        } else if (call.state === "connected") {
          await this.sipService.holdCall(call.id);
        }
      }
    } catch (error) {
      console.error("Error toggling call:", error);
    }
  }

  get canCreateConference(): boolean {
    return (
      this.activeCalls.length >= 2 &&
      this.activeCalls.filter(
        (call) => call.state === "connected" || call.state === "held",
      ).length >= 2
    );
  }

  async createConference(): Promise<void> {
    if (!this.canCreateConference) return;

    try {
      await this.sipService.createConference();
    } catch (error) {
      console.error("Error creating conference:", error);
    }
  }

  // ==================== Keypad Methods ====================

  openKeypad(): void {
    const activeCall = this.activeCalls.length > 0 ? this.activeCalls[0] : null;
    const data: KeypadDialogData = {
      targetNumber: this.targetNumber,
      hasActiveCall: !!activeCall,
      activeCallId: activeCall?.id ?? null,
      lastDialedNumber: this.lastDialedNumber,
    };
    const dialogRef = this.dialog.open(KeypadDialogComponent, {
      data,
      panelClass: ["adx-keypad-dialog-panel", "adx-dialog-small"],
    });
    dialogRef.afterClosed().subscribe((result?: KeypadDialogResult) => {
      if (result?.lastDialedNumber) {
        this.lastDialedNumber = result.lastDialedNumber;
      }
    });
  }

  // ==================== Video Call Methods ====================

  /**
   * Start a video call with the target number
   */
  async startVideoCall(): Promise<void> {
    if (!this.canMakeCall()) {
      return;
    }

    // Handle redial behavior - same as audio call button
    if (!this.targetNumber) {
      if (this.lastDialedNumber) {
        this.targetNumber = this.lastDialedNumber;
        return;
      }
      return;
    }

    try {
      // Lazy detection - check video capabilities just-in-time
      await this.initializeVideoCapabilities();

      if (!this.canStartVideoCall) {
        console.warn("Video calling not supported");
        // TODO: Show user-friendly error dialog
        return;
      }

      await this.sipService.makeCall(toDialable(this.targetNumber), true); // true = with video
      this.lastDialedNumber = this.targetNumber;
      this.lastCallWasVideo = true;
      this.targetNumber = "";
    } catch (error) {
      console.error("Error starting video call:", error);
      // Show error to user
    }
  }

  /**
   * Upgrade an active audio call to video
   */
  async upgradeToVideo(callId: string): Promise<void> {
    try {
      // Lazy detection - check video capabilities just-in-time
      await this.initializeVideoCapabilities();

      if (!this.canStartVideoCall) {
        console.warn("Video calling not supported");
        // TODO: Show user-friendly error dialog
        return;
      }

      console.log(`[PhoneWidget] Upgrading to video (auto quality)`);
      await this.sipService.upgradeToVideo(callId);
    } catch (error) {
      console.error("Error upgrading to video:", error);
    }
  }

  /**
   * Downgrade a video call to audio-only
   */
  async downgradeToAudio(callId: string): Promise<void> {
    try {
      await this.sipService.downgradeToAudio(callId);
    } catch (error) {
      console.error("Error downgrading to audio:", error);
    }
  }

  /**
   * Toggle video mute/unmute
   */
  async toggleVideoMute(callId: string): Promise<void> {
    try {
      await this.sipService.toggleVideoMute(callId);
    } catch (error) {
      console.error("Error toggling video mute:", error);
    }
  }

  /**
   * Switch camera for active video call
   */
  async switchCamera(callId: string, deviceId: string): Promise<void> {
    try {
      await this.sipService.updateCallVideoInput(callId, deviceId);
    } catch (error) {
      console.error("Error switching camera:", error);
    }
  }

  /**
   * Setup fullscreen change event listener
   * This tracks when user exits fullscreen via ESC key or browser controls
   */
  private setupFullscreenListener(): void {
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  }

  /**
   * Handle fullscreen state changes
   */
  private handleFullscreenChange = (): void => {
    this.ngZone.run(() => {
      this.isVideoFullscreen = !!document.fullscreenElement;
    });
  };

  /**
   * Toggle fullscreen video mode using browser's native Fullscreen API
   */
  async toggleVideoFullscreen(): Promise<void> {
    if (!this.videoSection?.nativeElement) {
      console.error("Video section element not found");
      return;
    }

    const element = this.videoSection.nativeElement as HTMLElement;

    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        await element.requestFullscreen();
        this.isVideoFullscreen = true;
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
        this.isVideoFullscreen = false;
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  }

  /**
   * Undock video to separate window
   */
  undockVideo(): void {
    // TODO: Implement in future milestone
    console.log("Undock video - to be implemented");
  }

  /**
   * Open video settings dialog
   */
  openVideoSettings(): void {
    // TODO: Open PhoneWidgetSettingsComponent with video tab selected
    console.log("Open video settings - to be implemented");
  }

  /**
   * Handle mouse movement over video area to show controls
   */
  onVideoAreaMouseMove(): void {
    if (this.videoControlsComponent) {
      this.videoControlsComponent.onMouseMove();
    }
  }

  /**
   * Handle video controls visibility change
   */
  onVideoControlsVisibilityChange(isVisible: boolean): void {
    this.areVideoControlsVisible = isVisible;
  }

  ngOnDestroy() {
    // Clean up subscription for plugin events
    this.destroyPluginEventSubscription$.next();
    this.destroyPluginEventSubscription$.complete();

    // Clean up all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    // Schedule delayed SIP stop — will be cancelled if widget is re-created
    // during a view switch, but will fire if widget is permanently removed
    this.sipService.scheduleStop();

    // Clean up ResizeObserver
    this.resizeObserver?.disconnect();

    // Clean up fullscreen listener
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
  }
}
